import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import UploadModal from './UploadModal';
import { resolveImageUrl } from '../utils/urlResolver';
import { X, Upload } from 'lucide-react';

const ArtworkPickerModal = ({ isOpen, onClose, selectedFeature, onSelectArtwork }) => {
  const { user } = useAuth();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const featureNames = {
    ways: 'Ways of Seeing',
    appreciation: 'Art Appreciation',
    reimagine: 'Reimagine'
  };

  const featureName = featureNames[selectedFeature] || 'this feature';

  const loadArtworks = async () => {
    if (!user) {
      console.log('[ArtworkPickerModal] No user, clearing artworks');
      setArtworks([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      console.log('[ArtworkPickerModal] Loading artworks for user:', user.uid);
      
      // Fetch artworks WITHOUT orderBy (no index required) - sort client-side
      const [artworksQuery, sharedArtworksQuery] = await Promise.all([
        getDocs(query(
          collection(db, 'artworks'),
          where('uid', '==', user.uid)
        )),
        getDocs(query(
          collection(db, 'sharedArtworks'),
          where('uid', '==', user.uid)
        ))
      ]);
      
      console.log('[ArtworkPickerModal] Fetched:', {
        artworks: artworksQuery.docs.length,
        sharedArtworks: sharedArtworksQuery.docs.length
      });
      
      const artworksData = [
        ...artworksQuery.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title,
            description: data.description,
            imageUrl: data.imageUrl,
            createdAt: data.createdAt,
            source: 'artworks',
            ...data
          };
        }),
        ...sharedArtworksQuery.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.artworkTitle || data.title,
            description: data.artworkDescription || data.description,
            imageUrl: data.artworkUrl || data.imageUrl,
            createdAt: data.createdAt,
            source: 'sharedArtworks',
            ...data
          };
        })
      ];
      
      // Sort by createdAt if available, then limit
      const sortedArtworks = artworksData
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
          return bTime - aTime;
        })
        .slice(0, 12);
      
      console.log('[ArtworkPickerModal] Final artworks count:', sortedArtworks.length);
      console.log('[ArtworkPickerModal] Artworks data:', sortedArtworks);
      setArtworks(sortedArtworks);
    } catch (error) {
      console.error('[ArtworkPickerModal] Error loading artworks:', error);
      console.error('[ArtworkPickerModal] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setArtworks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      loadArtworks();
    } else if (!isOpen) {
      // Reset when modal closes
      setArtworks([]);
      setLoading(true);
    }
  }, [isOpen, user]);

  const handleArtworkSelect = (artwork) => {
    onSelectArtwork(artwork);
    onClose();
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    loadArtworks(); // Reload artworks after upload
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="bg-white rounded-[32px] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 253, 247, 0.98) 100%)',
                border: '2px solid rgba(255, 204, 128, 0.3)',
                boxShadow: '0 20px 60px rgba(31, 41, 55, 0.2), 0 0 0 1px rgba(255, 204, 128, 0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-[rgba(31,41,55,0.08)] flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-[#1F2937]">
                    Choose an artwork for {featureName}
                  </h2>
                  <p className="text-sm text-[#4B5563] mt-1">
                    Select one of your artworks to begin
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-[rgba(31,41,55,0.08)] rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-[#1F2937]" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Upload Button - Single button, always visible */}
                <motion.button
                  onClick={() => setShowUploadModal(true)}
                  className="w-full mb-6 rounded-[24px] px-6 py-4 bg-gradient-to-r from-[#F7E6A1] via-[#FFE7B3] to-[#FFD7C8] text-[#1F2937] font-bold text-base shadow-lg border-2 border-white/70 flex items-center justify-center gap-3"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Upload className="w-5 h-5" />
                  {artworks.length === 0 ? 'Upload Your First Artwork' : 'Upload New Artwork'}
                </motion.button>

                {/* Your Artworks Section */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-[#1F2937] mb-4">
                    Your Artworks {artworks.length > 0 && `(${artworks.length})`}
                  </h3>

                  {loading ? (
                    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className="flex-shrink-0 w-32 h-32 rounded-[20px] bg-gradient-to-br from-[#CFE9FF]/30 to-[#E7D9FF]/30 animate-pulse"
                        />
                      ))}
                    </div>
                  ) : artworks.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#F7E6A1] to-[#FFE7B3] opacity-60 flex items-center justify-center">
                        <Upload className="w-12 h-12 text-[#1F2937]/40" />
                      </div>
                      <p className="text-[#4B5563] text-lg">
                        You don't have any artworks yet. Click the button above to upload your first artwork!
                      </p>
                    </div>
                  ) : (
                    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory scroll-smooth" style={{ WebkitOverflowScrolling: 'touch' }}>
                      {artworks.map((artwork) => (
                        <motion.button
                          key={artwork.id}
                          onClick={() => handleArtworkSelect(artwork)}
                          className="flex-shrink-0 w-32 h-32 rounded-[20px] overflow-hidden border-2 border-white/50 shadow-md hover:shadow-xl transition-all group relative snap-center"
                          style={{
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(250, 250, 255, 0.9) 100%)'
                          }}
                          whileHover={{ scale: 1.05, y: -4 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="w-full h-full relative">
                            {artwork.imageUrl || artwork.artworkUrl ? (
                              <img
                                src={resolveImageUrl(artwork.imageUrl || artwork.artworkUrl)}
                                alt={artwork.title || 'Artwork'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextElementSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="w-full h-full bg-gradient-to-br from-[#CFE9FF]/60 to-[#E7D9FF]/50 flex items-center justify-center"
                              style={{ display: artwork.imageUrl || artwork.artworkUrl ? 'none' : 'flex' }}
                            >
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F7E6A1] to-[#FFE7B3] opacity-70" />
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                              <p className="text-white text-xs font-semibold truncate">
                                {artwork.title || 'Untitled'}
                              </p>
                            </div>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Upload Modal */}
          {showUploadModal && (
            <UploadModal
              onClose={handleUploadComplete}
            />
          )}
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ArtworkPickerModal;
